import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  /**
   * Accordions that should open immediately without scroll-into-view
   * (FAQ page, PDP product column, PDP product-info section).
   */
  get #shouldSkipAccordionScroll() {
    return Boolean(
      this.closest(
        'faq-section, .product-information, #product-info-accordion, [data-skip-accordion-scroll]'
      )
    );
  }

  #controller = new AbortController();
  #isInitialized = false;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  #toggleInProgress = false;

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    // Close sibling accordions when one opens
    this.details.addEventListener('toggle', this.#handleToggle, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Initialize ResizeObserver for scroll handling during animations
    this.#initResizeObserver();

    // Set initialized flag after a short delay to prevent scroll on page load
    setTimeout(() => {
      this.#isInitialized = true;
    }, 100);
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
    // Disconnect the ResizeObserver
    this.#resizeObserver?.disconnect();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Stop event from bubbling up to parent elements (prevents closing menu drawer)
    event.stopPropagation();

    // If accordion is closed and will open, handle manually (always prevent native toggle)
    if (!this.details.open && this.#isInitialized) {
      event.preventDefault();

      const allowMultipleOpen =
        this.hasAttribute('data-allow-multiple-open') ||
        this.closest('[data-allow-multiple-open="true"]');

      if (allowMultipleOpen) {
        this.details.open = true;
        return;
      }

      const isMobile = isMobileBreakpoint();
      const isDesktop = !isMobile;

      // Don't proceed if accordion behavior is disabled
      if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
        return;
      }

      // PDP / FAQ: open immediately, but pre-calculate the final scroll target
      // *before* touching the DOM. Any open sibling above this accordion will
      // collapse (losing its content height), so we subtract that delta upfront.
      // This avoids chasing the layout during CSS transitions.
      if (this.#shouldSkipAccordionScroll) {
        const offset = this.#stickyHeaderHeight(isMobileBreakpoint() ? 8 : 16);

        const accordionGroup =
          this.closest('[data-accordion-group]') ||
          this.closest('faq-section') ||
          this.closest('.block') ||
          this.parentElement;

        let collapseHeight = 0;
        const thisTop = this.getBoundingClientRect().top;

        if (accordionGroup) {
          accordionGroup.querySelectorAll('accordion-custom').forEach((acc) => {
            if (acc === this || this.contains(acc)) return;
            const details = acc.querySelector('details');
            if (!details?.open) return;
            // Only count siblings whose top is above this accordion
            if (acc.getBoundingClientRect().top < thisTop) {
              const content = acc.querySelector('.details-content');
              if (content) collapseHeight += content.getBoundingClientRect().height;
            }
          });
        }

        // Final document position of this accordion after sibling collapses
        const targetScrollY =
          thisTop + window.scrollY - collapseHeight - offset;

        this.#closeSiblingAccordions();
        this.details.open = true;

        this.#scrollTo(targetScrollY);
        return;
      }

      // Step 1: Scroll to first accordion in group
      this.#scrollIntoViewWithOffset();

      // Step 2: Close sibling accordions after scroll starts
      setTimeout(() => {
        this.#closeSiblingAccordions();
      }, 100);

      // Step 3: Open this accordion after siblings are closed
      setTimeout(() => {
        this.details.open = true;
      }, 300);
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const hasOpenByDefaultMobile = this.hasAttribute('open-by-default-on-mobile');
    const hasOpenByDefaultDesktop = this.hasAttribute('open-by-default-on-desktop');
    const isMobile = isMobileBreakpoint();

    // If both attributes exist, always keep open (used in header mega menu context)
    if (hasOpenByDefaultMobile && hasOpenByDefaultDesktop) {
      this.details.open = true;
      return;
    }

    const shouldBeOpen =
      (isMobile && hasOpenByDefaultMobile) ||
      (!isMobile && hasOpenByDefaultDesktop);

    this.details.open = shouldBeOpen;
  }

  /**
   * Initializes the ResizeObserver to handle scroll during CSS animations
   */
  // #initResizeObserver() {
  //   this.#resizeObserver = new ResizeObserver(() => {
  //     // Only scroll if accordion is open, initialized, and toggle is in progress
  //     if (this.details.open && this.#isInitialized && this.#toggleInProgress) {
  //       this.#scrollIntoViewWithOffset();
  //     }
  //   });

  //   // Observe the details element for size changes during animations
  //   this.#resizeObserver.observe(this.details);
  // }

  #initResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.details.open || !this.#isInitialized || !this.#toggleInProgress) {
        return;
      }

      if (this.#shouldSkipAccordionScroll) {
        return;
      }

      // defer side effects
      requestAnimationFrame(() => {
        this.#scrollIntoViewWithOffset();
      });
    });

    // Observe the details element for size changes during animations
    this.#resizeObserver.observe(this.details);
  }

  /**
   * Closes sibling accordions in the same group
   */
  #closeSiblingAccordions() {
    if (this.hasAttribute('data-allow-multiple-open') || this.closest('[data-allow-multiple-open="true"]')) {
      return;
    }

    // Find the closest accordion group container
    const accordionGroup =
      this.closest('[data-accordion-group]') ||
      this.closest('faq-section') ||
      this.closest('.block') ||
      this.parentElement;

    if (!accordionGroup) return;

    /** @type {NodeListOf<HTMLElement>} */
    const siblingAccordions = accordionGroup.querySelectorAll('accordion-custom');

    siblingAccordions.forEach((accordion) => {
      // Skip if it's the current accordion
      if (accordion === this) return;

      // Skip if this accordion is a descendant of the current accordion (nested)
      if (this.contains(accordion)) return;

      // Skip if not a child of the same group
      if (!accordionGroup.contains(accordion)) return;

      const details = accordion.querySelector('details');
      if (details && details.open) {
        details.removeAttribute('open');
      }
    });
  }

  /**
   * Smooth-scrolls to `top` while freezing the header's scroll-show/hide logic for the
   * duration of the animation. This prevents the header from flashing as programmatic
   * scroll events fire during the transition.
   *
   * Uses the native `scrollend` event to know when the scroll is done; a safety timeout
   * covers older browsers that may not support it.
   *
   * @param {number} top - The target scrollY position in pixels.
   */
  #scrollTo(top) {
    const headerEl = document.querySelector('#header-component');

    if (headerEl) {
      headerEl.setAttribute('data-accordion-scrolling', '');

      let safetyTimer;
      const unlock = () => {
        clearTimeout(safetyTimer);
        headerEl.removeAttribute('data-accordion-scrolling');
      };

      window.addEventListener('scrollend', unlock, { once: true });
      safetyTimer = setTimeout(unlock, 1500);
    }

    window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  }

  /**
   * Returns the current height of the sticky header plus a small breathing-room gap.
   * Measured synchronously at call-time so it is never affected by animations or scroll position.
   * Falls back to a safe static value if the header element is not found.
   */
  #stickyHeaderHeight(extra = 16) {
    const header =
      document.querySelector('.header-section') ||
      document.querySelector('header-component') ||
      document.querySelector('header');
    const height = header ? Math.round(header.getBoundingClientRect().height) : 0;
    return (height || (isMobileBreakpoint() ? 70 : 100)) + extra;
  }

  #scrollIntoViewWithOffset() {
    const offset = this.#stickyHeaderHeight(isMobileBreakpoint() ? 16 : 24);

    // Find the accordion group
    const accordionGroup =
      this.closest('[data-accordion-group]') ||
      this.closest('faq-section') ||
      this.closest('.block') ||
      this.parentElement;

    // Find the first accordion in the group
    const firstAccordion = accordionGroup?.querySelector('accordion-custom');
    const targetElement = firstAccordion || this;

    const top =
      targetElement.getBoundingClientRect().top +
      window.pageYOffset -
      offset;

    if (accordionGroup?.closest('.menu-drawer__menu')) return;
    this.#scrollTo(top);
  }

  /**
   * Ensures only one accordion is open at a time within the same accordion group
   * Looks for closest parent container with data-accordion-group attribute
   * If not found, scopes to the parent block element
   */
  #handleToggle = () => {
    if (!this.details.open) return;

    if (!this.#isInitialized) return;

    // Mark that toggle is in progress for ResizeObserver
    this.#toggleInProgress = true;

    // Reset the flag after animation completes (typically 300-500ms)
    setTimeout(() => {
      this.#toggleInProgress = false;
    }, 500);
  };

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
